import { ForwardedRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutePaths } from '../../../../../../constants';
import styles from './Message.module.scss';
import cx from 'classnames';
import { Icon, Text } from 'components';
import { Icons } from 'components/Icon/Icon';
import { useDevice } from 'hooks';
import { appActions } from 'store/app/slice';
import { getUsers } from 'store/users/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { FeedItem, TypeOfMessages, UserRole } from 'types';
import { getFormattedDate } from 'utils/getFormattedData';

type Props = {
  feedItem: FeedItem;
};

const messageIcon: Record<TypeOfMessages, Icons> = {
  AUDIO: 'File',
  FILE: 'File',
  LINK: 'Link',
  PHOTO: 'Image',
  TEXT: 'File',
  VIDEO: 'Image',
};

export const Message = forwardRef(
  ({ feedItem: { message, patientCase } }: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const { isTablet } = useDevice();

    const users = useSelector(getUsers);

    const sender = users.find((user) => user.id === message.createdBy);

    const senderName: string = sender?.name || '';

    const [name, surname] = senderName.split(' ');

    const senderInitials = (name?.[0] ?? '') + (surname ? surname?.[0] : '');
    const senderRole = sender?.role === UserRole.CLINIC ? 'STUDIO' : sender?.role;
    const sentDate = getFormattedDate(message.createdAt);

    const patientName = `${patientCase.firstName} ${patientCase.lastName}`;
    const patientCaseExternalId = patientCase.externalPatientId;

    const icon = messageIcon[message.type];

    const isNotTextMessage = message.type !== TypeOfMessages.TEXT;

    const messageTextContent =
      message.body || (message.type === TypeOfMessages.LINK ? message.attachmentLink : message.attachmentFile.name);

    const onMessageCardClickHandler = () => {
      dispatch(appActions.setShowPatientCaseList(false));
      dispatch(appActions.setCurrentPatientTab('discussion'));
      navigate(`${RoutePaths.PATIENTS}/${patientCase.id}`, {
        state: {
          path: location.pathname,
        },
      });
    };

    return (
      <div className={styles.container} ref={ref} onClick={onMessageCardClickHandler}>
        <div className={styles.header}>
          <div className={styles.sender}>
            <div className={styles.avatar}>
              <Text type="button" className={styles.initials}>
                {senderInitials}
              </Text>
            </div>
            <div className={styles.senderInfo}>
              <Text type="button" className={styles.senderName}>
                {senderName}
              </Text>
              <Text type="caption" className={styles.senderRole}>
                {senderRole}
              </Text>
            </div>
          </div>
          {!isTablet && (
            <Text type="caption" className={styles.date}>
              {sentDate}
            </Text>
          )}
        </div>
        <div className={styles.messageTextContainer}>
          <div className={styles.messageContent}>
            {isNotTextMessage && <Icon name={icon} className={styles.messageIcon} />}
            <Text type="body1" className={cx(styles.messageText, { [styles.isNotTextMessage]: isNotTextMessage })}>
              {messageTextContent}
            </Text>
          </div>

          {isTablet && (
            <Text type="caption" className={styles.date}>
              {sentDate}
            </Text>
          )}
        </div>
        <div className={styles.divider} />
        <div className={styles.footer}>
          <div className={styles.patientCaseInfo}>
            <Text type="h5" className={styles.externalId}>
              {patientCaseExternalId}
            </Text>
            <Text type="body1" className={styles.patientName}>
              {patientName}
            </Text>
          </div>
          <Icon name="ArrowRight" className={styles.arrowRight} />
        </div>
      </div>
    );
  },
);

Message.displayName = 'Message';
